import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Memory Match (Card Flip Game)
 * Single-file React component with built‑in CSS (no Tailwind required)
 * - Difficulty selector (Easy 4x4, Medium 5x4, Hard 6x6)
 * - Move counter, timer, best score (localStorage)
 * - Smooth flip animation, keyboard & screen-reader friendly
 */

const EMOJIS = [
  "🐶","🐱","🦊","🐻","🐼","🐨","🐯","🦁","🐸","🐵",
  "🦄","🐷","🐔","🐧","🐙","🦋","🐝","🐞","🌸","🌻",
  "🍎","🍊","🍉","🍇","🍓","🥝","🥑","🍔","🍕","🍩",
  "⚽️","🏀","🏈","🎲","🎯","🎹","🎧","🚗","✈️","🚀",
];

const DIFFICULTIES = {
  easy:   { label: "Easy",   rows: 4, cols: 4 },   // 8 pairs
  medium: { label: "Medium", rows: 5, cols: 4 },   // 10 pairs
  hard:   { label: "Hard",   rows: 6, cols: 6 },   // 18 pairs
};

function shuffle(array) {
  const a = array.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function useInterval(callback, delay) {
  const saved = useRef();
  useEffect(() => { saved.current = callback; }, [callback]);
  useEffect(() => {
    if (delay == null) return;
    const id = setInterval(() => saved.current && saved.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}

// ====== Component ======
export default function MemoryMatch() {
  const [difficulty, setDifficulty] = useState("easy");
  const cfg = DIFFICULTIES[difficulty];
  const totalCards = cfg.rows * cfg.cols;
  const pairCount = totalCards / 2;

  const [deck, setDeck] = useState([]);
  const [revealed, setRevealed] = useState([]);
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [running, setRunning] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [won, setWon] = useState(false);

  const bestKey = `mm_best_${difficulty}`;
  const best = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(bestKey) || "null"); } catch { return null; }
  }, [bestKey]);

  useInterval(() => { if (running) setSeconds((s) => s + 1); }, 1000);

  useEffect(() => { newGame(difficulty); }, []);

  function makeDeck(rows, cols) {
    const needed = (rows * cols) / 2;
    const values = shuffle(EMOJIS).slice(0, needed);
    const pairs = values.flatMap((v, i) => [
      { id: `${i}-a`, value: v, matched: false },
      { id: `${i}-b`, value: v, matched: false },
    ]);
    return shuffle(pairs);
  }

  function newGame(diff = difficulty) {
    const c = DIFFICULTIES[diff];
    const d = makeDeck(c.rows, c.cols);
    setDeck(d);
    setRevealed([]);
    setMoves(0);
    setMatches(0);
    setRunning(false);
    setSeconds(0);
    setWon(false);
  }

  function onChangeDifficulty(e) {
    const diff = e.target.value;
    setDifficulty(diff);
    newGame(diff);
  }

  function onCardClick(idx) {
    if (won) return;
    if (!running) setRunning(true);
    setRevealed((r) => {
      if (r.includes(idx)) return r;
      if (deck[idx].matched) return r;
      if (r.length === 2) return r;
      const next = [...r, idx];
      if (next.length === 2) {
        setMoves((m) => m + 1);
        const [i1, i2] = next;
        if (deck[i1].value === deck[i2].value) {
          setTimeout(() => {
            setDeck((d) => {
              const nd = d.slice();
              nd[i1] = { ...nd[i1], matched: true };
              nd[i2] = { ...nd[i2], matched: true };
              return nd;
            });
            setMatches((mm) => mm + 1);
            setRevealed([]);
          }, 220);
        } else {
          setTimeout(() => setRevealed([]), 700);
        }
      }
      return next;
    });
  }

  useEffect(() => {
    if (matches === pairCount && pairCount > 0) {
      setWon(true);
      setRunning(false);
      const attempt = { moves, seconds };
      try {
        const prev = JSON.parse(localStorage.getItem(bestKey) || "null");
        const better = !prev || attempt.moves < prev.moves || (attempt.moves === prev.moves && attempt.seconds < prev.seconds);
        if (better) localStorage.setItem(bestKey, JSON.stringify(attempt));
      } catch {}
    }
  }, [matches, pairCount, moves, seconds, bestKey]);

  function formatTime(s) {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const ss = (s % 60).toString().padStart(2, "0");
    return `${m}:${ss}`;
  }

  const gridStyle = { gridTemplateColumns: `repeat(${cfg.cols}, minmax(0, 1fr))` };

  return (
    <div className="mm">
      {/* Built-in styles */}
      <style>{STYLE}</style>

      <header className="mm-header">
        <div>
          <h1>Memory Match</h1>
          <p className="mm-sub">Flip cards to find all matching pairs.</p>
        </div>
        <div className="mm-controls">
          <select aria-label="Select difficulty" value={difficulty} onChange={onChangeDifficulty}>
            {Object.entries(DIFFICULTIES).map(([key, val]) => (
              <option key={key} value={key}>{val.label} ({val.rows}×{val.cols})</option>
            ))}
          </select>
          <button className="mm-btn" onClick={() => newGame()} aria-label="Restart game">Restart</button>
        </div>
      </header>

      <section className="mm-stats">
        <Stat label="Moves" value={moves} />
        <Stat label="Time" value={formatTime(seconds)} />
        <Stat label="Matches" value={`${matches}/${pairCount}`} />
      </section>

      {best && (
        <div className="mm-best">Best on {DIFFICULTIES[difficulty].label}: <b>{best.moves} moves</b>, <b>{formatTime(best.seconds)}</b></div>
      )}

      <main className="mm-grid" style={gridStyle} role="grid" aria-label="Card grid">
        {deck.map((card, idx) => (
          <Card
            key={card.id}
            index={idx}
            value={card.value}
            revealed={revealed.includes(idx) || card.matched}
            matched={card.matched}
            onClick={() => onCardClick(idx)}
          />
        ))}
      </main>

      <footer className="mm-footer">
        {won ? (
          <div className="mm-win">
            <span className="mm-emoji">🎉</span>
            <span>You won in <b>{moves}</b> moves and <b>{formatTime(seconds)}</b>!</span>
            <button className="mm-btn" onClick={() => newGame()}>Play again</button>
          </div>
        ) : (
          <span>Tip: Try to beat your best score by planning your flips.</span>
        )}
      </footer>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="mm-stat">
      <div className="mm-stat-label">{label}</div>
      <div className="mm-stat-value">{value}</div>
    </div>
  );
}

function Card({ value, revealed, matched, onClick }) {
  function onKeyDown(e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }
  return (
    <button
      role="gridcell"
      aria-label={revealed ? `Card showing ${value}` : "Hidden card"}
      aria-pressed={revealed}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className="mm-card"
    >
      <div className={"mm-card-inner" + (revealed ? " mm-card-inner--flipped" : "") + (matched ? " mm-card-inner--matched" : "") }>
        <div className="mm-card-back">❓</div>
        <div className="mm-card-front"><span className={matched ? "mm-pulse" : ""}>{value}</span></div>
      </div>
    </button>
  );
}

// ====== CSS (scoped) ======
const STYLE = `
:root{
  --mm-bg: linear-gradient(135deg,#f8fafc,#e2e8f0);
  --mm-text:#0f172a; --mm-muted:#64748b;
  --mm-card:#ffffff; --mm-border:#e2e8f0; --mm-accent:#111827; --mm-ring:#34d399;
}
@media (prefers-color-scheme: dark){
  :root{ --mm-bg: linear-gradient(135deg,#0b1220,#0f172a); --mm-text:#e5e7eb; --mm-muted:#94a3b8; --mm-card:#0f172a; --mm-border:#1f2937; --mm-accent:#e5e7eb; }
}
.mm{ min-height:100vh; display:flex; flex-direction:column; align-items:center; gap:16px; padding:32px 16px; background:var(--mm-bg); color:var(--mm-text); font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; }
.mm-header{ width:100%; max-width:1000px; display:flex; flex-wrap:wrap; align-items:end; justify-content:space-between; gap:16px; margin-bottom:8px; }
.mm-header h1{ font-size: clamp(24px, 4vw, 34px); margin:0; font-weight:800; letter-spacing:-0.02em; }
.mm-sub{ margin:4px 0 0; color:var(--mm-muted); }
.mm-controls{ display:flex; gap:12px; align-items:center; }
.mm-controls select{ padding:8px 12px; border-radius:16px; border:1px solid var(--mm-border); background:#fff; color:#111; box-shadow:0 1px 2px rgba(0,0,0,.05); }
.mm-btn{ padding:8px 14px; border-radius:16px; border:0; background:var(--mm-accent); color:#fff; font-weight:600; box-shadow:0 2px 6px rgba(0,0,0,.12); cursor:pointer; }
.mm-btn:hover{ filter:brightness(1.05); }
.mm-btn:active{ transform:scale(.98); }

.mm-stats{ width:100%; max-width:1000px; display:grid; grid-template-columns: repeat(3, 1fr); gap:12px; margin-bottom:8px; }
.mm-stat{ background:rgba(255,255,255,.75); backdrop-filter:saturate(1.4) blur(4px); border:1px solid var(--mm-border); border-radius:16px; padding:14px; box-shadow:0 1px 4px rgba(0,0,0,.06); }
.mm-stat-label{ font-size:12px; text-transform:uppercase; letter-spacing:.08em; color:var(--mm-muted); }
.mm-stat-value{ font-size:22px; font-weight:700; line-height:1.1; }

.mm-best{ width:100%; max-width:1000px; color:var(--mm-muted); margin-bottom:4px; font-size:14px; }

.mm-grid{ width:100%; max-width:1000px; display:grid; gap:12px; }

.mm-card{ position:relative; width:100%; aspect-ratio:3/4; border:0; background:transparent; padding:0; cursor:pointer; outline:none; border-radius:18px; }
.mm-card:focus-visible{ box-shadow:0 0 0 3px #94a3b8; }
.mm-card-inner{ position:absolute; inset:0; border-radius:18px; transform-style:preserve-3d; transition: transform .5s; }
.mm-card-inner--flipped{ transform:rotateY(180deg); }
.mm-card-inner--matched{ box-shadow:0 0 0 3px var(--mm-ring) inset; }
.mm-card-back, .mm-card-front{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; border-radius:18px; border:1px solid var(--mm-border); backface-visibility:hidden; box-shadow:0 2px 8px rgba(0,0,0,.08); }
.mm-card-back{ background:#0f172a; color:#fff; font-size:22px; }
.mm-card-front{ background:var(--mm-card); transform:rotateY(180deg); font-size:46px; }

.mm-footer{ margin-top:8px; color:var(--mm-muted); font-size:14px; text-align:center; }
.mm-win{ display:inline-flex; align-items:center; gap:10px; padding:10px 14px; background:rgba(255,255,255,.8); backdrop-filter: blur(6px); border:1px solid var(--mm-border); border-radius:16px; color:var(--mm-text); box-shadow:0 2px 10px rgba(0,0,0,.08); }
.mm-emoji{ font-size:20px; }

@keyframes mmPulse { 0%{ transform:scale(1); } 50%{ transform:scale(1.08); } 100%{ transform:scale(1); } }
.mm-pulse{ animation:mmPulse .6s ease; }

@media (max-width: 640px){
  .mm-stats{ grid-template-columns: 1fr; }
}

@media (prefers-reduced-motion: reduce){
  .mm-card-inner{ transition:none; }
  .mm-pulse{ animation:none; }
}
`;
